import { Box, Typography, Paper, useTheme, LinearProgress, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import VerticalStackedBarChart from "../../components/chart/VerticalStackedBarChart";
import HorizontalStackedBarChart from "../../components/chart/HorizontalStackedBarChart";
import DonutChart from "../../components/chart/DonutChart";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

const OverallPerformanceAnalytics = ({ data = [], entityType = "organization" }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);
    const isSupplyChain = entityType === "supply_chain";
    const entityLabel = isSupplyChain ? t("Supply Chain") : t("Organization");
    const entityLabelPlural = isSupplyChain ? t("Supply Chains") : t("Organizations");

    // Mock-up transformation of data if not already in the right format
    const chartData = data.map((item, index) => ({
        name: item.name || `Org ${index + 1}`,
        label: item.name || `Org ${index + 1}`, // Needed for Horizontal Chart
        "Scope 1": Math.max(0, (Number(item.scope_1) || 0) / 1000),
        "Scope 2": Math.max(0, (Number(item.scope_2) || 0) / 1000),
        "Scope 3": Math.max(0, (Number(item.scope_3) || 0) / 1000),
    }));

    const scopeTotals = {
        "Scope 1": chartData.reduce((acc, curr) => acc + curr["Scope 1"], 0),
        "Scope 2": chartData.reduce((acc, curr) => acc + curr["Scope 2"], 0),
        "Scope 3": chartData.reduce((acc, curr) => acc + curr["Scope 3"], 0),
    };

    const colors = ["#22c55e", "#3b82f6", "#f97316"]; // Green, Blue, Orange

    const totalPortfolio = Object.values(scopeTotals).reduce((a, b) => a + b, 0);

    const scopeAverages = {
        "Scope 1": chartData.length > 0 ? scopeTotals["Scope 1"] / chartData.length : 0,
        "Scope 2": chartData.length > 0 ? scopeTotals["Scope 2"] / chartData.length : 0,
        "Scope 3": chartData.length > 0 ? scopeTotals["Scope 3"] / chartData.length : 0,
    };

    const donutData = [
        { label: "Scope 1", value: scopeTotals["Scope 1"], color: colors[0], percent: totalPortfolio > 0 ? (scopeTotals["Scope 1"] / totalPortfolio) * 100 : 0 },
        { label: "Scope 2", value: scopeTotals["Scope 2"], color: colors[1], percent: totalPortfolio > 0 ? (scopeTotals["Scope 2"] / totalPortfolio) * 100 : 0 },
        { label: "Scope 3", value: scopeTotals["Scope 3"], color: colors[2], percent: totalPortfolio > 0 ? (scopeTotals["Scope 3"] / totalPortfolio) * 100 : 0 },
    ];

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, animation: "org-fadeInUp 0.4s ease" }}>
            <Box sx={{ flex: "1 1 60%", minWidth: 450 }}>
                <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card }}>
                    <Typography variant="h6" sx={{ mb: 2, pb: 1, fontWeight: 500, color: tok.text.chartTitle, borderBottom: `1px solid ${tok.border.divider}` }}>{t("Emissions by")} {entityLabel}</Typography>
                    <HorizontalStackedBarChart
                        data={chartData}
                        dataKeys={["Scope 1", "Scope 2", "Scope 3"]}
                        getColor={(i) => colors[i]}
                        showLegend={true}
                        domain={[0, 'auto']}
                    />
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 4 }}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>{t("X-Axis:")} t CO₂e</Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>{t("Y-Axis:")} {entityLabelPlural}</Typography>
                    </Box>
                </Paper>
            </Box>

            <Box sx={{ flex: "1 1 30%", minWidth: 350 }}>
                <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card }}>
                    <Typography variant="h6" sx={{ mb: 4, pb: 1, fontWeight: 500, color: tok.text.chartTitle, borderBottom: `1px solid ${tok.border.divider}` }}>{t("Scope Distribution")}</Typography>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                        <DonutChart
                            data={donutData}
                            size={240}
                            strokeWidth={24}
                            centerContent={(
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" sx={{ color: 'textSecondary', display: 'block', fontWeight: 500 }}>{t("Total")}</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{totalPortfolio.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                                    <Typography variant="caption" sx={{ color: 'textSecondary', fontWeight: 500 }}>t CO₂e</Typography>
                                </Box>
                            )}
                        />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        {donutData.map((d, i) => (
                            <Box key={i} sx={{ mb: 2.5 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: d.color }} />
                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "13px" }}>{t(d.label)}</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "13px" }}>
                                        {d.value.toLocaleString(undefined, { maximumFractionDigits: 0 })} t CO₂e ({Math.round(d.percent)}%)
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={d.percent}
                                    sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                                        '& .MuiLinearProgress-bar': { bgcolor: d.color }
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>


                    <Divider sx={{ mt: 4, mb: 3, opacity: 0.4 }} />

                    {/* Portfolio Averages Row */}
                    <Box sx={{ mb: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 500,
                                fontSize: "13px",
                                color: "text.secondary",
                                display: "block",
                                mb: 2,
                                letterSpacing: "0.5px"
                            }}
                        >
                            {t("Portfolio Averages")}
                        </Typography>

                        <Box sx={{ display: "flex", gap: 2 }}>
                            {Object.entries(scopeAverages).map(([key, val], i) => (
                                <Box
                                    key={key}
                                    sx={{
                                        flex: 1,
                                        p: 1.5,
                                        borderRadius: "10px",
                                        border: "1px solid",
                                        borderColor: theme.palette.mode === "dark" ? "#3f3f46" : "#e5e7eb",
                                        bgcolor: theme.palette.mode === "dark"
                                            ? "rgba(255,255,255,0.03)"
                                            : "#fafafa"
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: colors[i],
                                            fontWeight: 500,
                                            fontSize: "12px"
                                        }}
                                    >
                                        {t(key)}
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "18px",
                                            mt: 0.5
                                        }}
                                    >
                                        {val.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                                        <Box
                                            component="span"
                                            sx={{ fontSize: "12px", opacity: 0.7, ml: 0.5 }}
                                        >
                                            tCO₂e
                                        </Box>
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default OverallPerformanceAnalytics;
